import { get, writable, type Writable } from 'svelte/store';
import { CheckoutErrorCodes } from '../../constants/checkout-error-codes';
import { AddressesFieldNames } from '../../constants/validation-schemas/addresses-field-names';
import { PersonalDetailsFieldNames } from '../../constants/validation-schemas/personal-details-field-names';
import type { Order } from '../../types';
import { getCheckoutContext } from '../getCheckoutContext';
import { handleOrderError } from '../handleOrderError';
import {
	fetchCart,
	type FetchCartParams,
	type FetchCartContext,
} from '../upsertCart';
import { Config } from '#lib/constants/config.js';
import { fetchWithRetry } from '$global/utils/fetchWithRetry/fetchWithRetry';
import { getCookie } from '$global/utils/getCookie/getCookie';
import { track } from '$global/utils/track';

export type UpsertOrderFromCartStore = ReturnType<
	typeof createUpsertOrderFromCartStore
>;

const ORDER_ALREADY_PROCESSED_MESSAGE = 'This order has already been processed';

const order = writable(null) as Writable<null | Order>;
const loading = writable(false);

export const createUpsertOrderFromCartStore = () => {
	const upsertOrderFromCart = async (
		params: FetchCartParams,
		context?: FetchCartContext | undefined,
		setCouponCodeInvalidError?: (reason: string) => void,
		allowAnyAllocation?: boolean | undefined,
		retryWithNewCart = true
	): Promise<Order | undefined> => {
		loading.update(() => true);

		const {
			cartPlatform: storedCartPlatform,
			cartItems: { cartId: storedCartId, clearCode },
			mixpanelEvents,
		} = getCheckoutContext();

		const cartId = context?.cartId || storedCartId;
		const cartPlatform = context?.cartPlatform || storedCartPlatform;

		const cartResponse = await fetchCart(
			params,
			{ cartPlatform, cartId },
			allowAnyAllocation
		);

		if (!cartResponse?.id) {
			return;
		}

		const reason = cartResponse?.coupon?.reason;

		if (!params.extraFields?.[PersonalDetailsFieldNames.PhoneNumber]) {
			track(mixpanelEvents.MissingPhoneNumber, { cart_id: get(cartId) });
		}

		if (reason?.startsWith('The coupon code provided has expired')) {
			return Promise.reject({
				code: CheckoutErrorCodes.CouponError,
				message: reason,
			});
		} else if (
			reason?.startsWith('The coupon code') &&
			setCouponCodeInvalidError
		) {
			setCouponCodeInvalidError(reason);
			clearCode();
		}

		if (window?.fbq && params.cartItems) {
			try {
				if (window.fbq?.instance?.pixelsByID?.[Config.MetaPixelId]?.userData) {
					window.fbq.instance.pixelsByID[
						Config.MetaPixelId
					].userData.external_id = cartResponse.id;

					window.fbq.instance.pixelsByID[Config.MetaPixelId].userData.em = (
						params.extraFields?.[PersonalDetailsFieldNames.Email] as string
					)?.toLowerCase();

					window.fbq.instance.pixelsByID[Config.MetaPixelId].userData.fn = (
						params.extraFields?.[PersonalDetailsFieldNames.FirstName] as string
					)?.toLowerCase();

					window.fbq.instance.pixelsByID[Config.MetaPixelId].userData.ln = (
						params.extraFields?.[PersonalDetailsFieldNames.LastName] as string
					)?.toLowerCase();

					window.fbq.instance.pixelsByID[Config.MetaPixelId].userData.ct = (
						params.extraFields?.[AddressesFieldNames.BillingCity] as string
					)?.toLowerCase();

					if (params.extraFields?.[AddressesFieldNames.BillingRegion]) {
						window.fbq.instance.pixelsByID[Config.MetaPixelId].userData.st = (
							params.extraFields?.[AddressesFieldNames.BillingRegion] as string
						)?.toLowerCase();
					}

					window.fbq.instance.pixelsByID[Config.MetaPixelId].userData.country =
						(
							params.extraFields?.[AddressesFieldNames.BillingCountry] as string
						)?.toLowerCase();

					window.fbq.instance.pixelsByID[Config.MetaPixelId].userData.ph =
						params.extraFields?.[PersonalDetailsFieldNames.PhoneNumber];
				}
			} catch (e) {
				console.error('upsertOrderFromCart: Cannot set fbq user data', e);
			}
		}

		const response = await fetchWithRetry(
			`${Config.GraphqlApiDomain}/ecomm/upsertOrderFromCart/${cartResponse.id}`,
			{
				method: 'PUT',
				headers: {
					'User-Agent': window.navigator.userAgent,
					'Content-type': 'application/json',
					fbp: getCookie('_fbp'),
				} as HeadersInit,
				body: JSON.stringify({
					visitor: '',
					request_id: '',
				}),
			}
		);

		const jsonResponse = await response.json();
		loading.update(() => false);

		if (response.ok) {
			order.set(jsonResponse);
			return Promise.resolve(jsonResponse);
		} else {
			const orderError = jsonResponse?.errors?.[0];

			// The persisted cart belongs to an order that has already gone through
			// (e.g. the customer paid but never reached the order-confirmed page,
			// which is what clears it). It can never be checked out again, so start
			// over with a new cart rather than leaving the customer stuck.
			if (
				retryWithNewCart &&
				orderError?.message?.startsWith(ORDER_ALREADY_PROCESSED_MESSAGE)
			) {
				cartId.set('');

				return upsertOrderFromCart(
					params,
					context,
					setCouponCodeInvalidError,
					allowAnyAllocation,
					false
				);
			}

			return handleOrderError(orderError);
		}
	};

	return { loading, order, upsertOrderFromCart };
};
